import type { FormManagementTabId } from '@/contracts/form-management'
import { useEffect, useState } from 'react'

export type SystemFeatureGroupId =
  | 'navigation'
  | 'share'
  | 'forms'
  | 'customers'
  | 'sales'
  | 'runtime'

export type SystemFeatureKey =
  | 'nav.forms'
  | 'nav.questionGroups'
  | 'nav.customerApplications'
  | 'nav.reviewCenter'
  | 'nav.customers'
  | 'nav.reviewNotifications'
  | 'nav.erpCustomerFields'
  | 'nav.admins'
  | 'nav.sales'
  | 'nav.settings'
  | 'nav.integrations'
  | 'share.link'
  | 'share.email'
  | 'share.qr'
  | 'share.tracking'
  | 'share.contract'
  | 'forms.analytics'
  | 'forms.export'
  | 'forms.exportLocalDownload'
  | 'forms.exportWorkerStream'
  | 'forms.exportFilters'
  | 'forms.exportStatus'
  | 'forms.startPage'
  | 'forms.crossAnalysis'
  | 'forms.visitorActivity'
  | 'forms.linkGenerator'
  | 'forms.completionPage'
  | 'forms.customerWorkflow'
  | 'forms.customerSubmitCustomerCompareFlow'
  | 'forms.operations'
  | 'forms.templates'
  | 'forms.logicVariables'
  | 'forms.logicCalculations'
  | 'forms.logicOutcomeBranches'
  | 'customers.createApplication'
  | 'customers.sourceRecords'
  | 'customers.duplicates'
  | 'customers.sourceFields'
  | 'customers.history'
  | 'customers.erpFiling'
  | 'sales.importCsv'
  | 'sales.fileImport'
  | 'runtime.statusBadges'
  | 'runtime.providerPlanning'
  | 'runtime.productionNotice'

export type SystemFeatureDefinition = {
  key: SystemFeatureKey
  label: string
  description: string
  defaultVisible: boolean
}

export type SystemFeatureGroup = {
  id: SystemFeatureGroupId
  label: string
  description: string
  features: SystemFeatureDefinition[]
}

export type SystemFeatureVisibility = Record<SystemFeatureKey, boolean>

export type SystemFeatureGroupSummary = {
  totalCount: number
  visibleCount: number
  hiddenCount: number
  changedCount: number
}

export const systemFeatureVisibilityStorageKey = 'miaoform.system.featureVisibility.v1'
const systemFeatureVisibilityEventName = 'miaoform:system-feature-visibility-change'

export const systemFeatureGroups: SystemFeatureGroup[] = [
  {
    id: 'navigation',
    label: '左側功能圖示',
    description: '控制 dashboard 左側主選單內非儀表板功能入口；系統入口固定保留。',
    features: [
      {
        key: 'nav.forms',
        label: '我的問卷',
        description: '左側「問卷」群組中的問卷清單入口；dashboard 本身不在此控制。',
        defaultVisible: true,
      },
      {
        key: 'nav.questionGroups',
        label: '題組管理',
        description: '左側「問卷」群組中的 reusable question group 管理入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.customerApplications',
        label: '新客戶申請',
        description: '左側「客戶」群組中的業務新客戶申請入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.reviewCenter',
        label: '審核中心',
        description: '左側「客戶」群組中的主管審核入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.customers',
        label: '客戶管理',
        description: '左側「客戶」群組中的客戶資料與來源問卷入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.reviewNotifications',
        label: '審核通知管理',
        description: '左側「客戶」群組中的審核通知查詢與再次發送入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.erpCustomerFields',
        label: '客戶欄位',
        description: '左側「ERP」群組中的客戶欄位對應設定入口；不連接真實 ERP。',
        defaultVisible: true,
      },
      {
        key: 'nav.admins',
        label: '管理員',
        description: '左側「管理」群組中的管理員權限設定入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.sales',
        label: '業務',
        description: '左側「管理」群組中的業務帳戶與主管關係設定入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.settings',
        label: '設定',
        description: '左側「管理」群組中的工作區與 provider planning 設定入口。',
        defaultVisible: true,
      },
      {
        key: 'nav.integrations',
        label: '串接管理',
        description: '左側「串接」群組中的 Webhook、n8n 與外部服務串接規格管理入口。',
        defaultVisible: true,
      },
    ],
  },
  {
    id: 'share',
    label: '問卷分享',
    description: '控制分享對話框內可見的分享連結與進階分享工具。',
    features: [
      {
        key: 'share.link',
        label: '分享連結',
        description: '公開 URL、開放狀態、截止日期、允許重複填寫與最大回應數設定。',
        defaultVisible: true,
      },
      {
        key: 'share.email',
        label: '寄送問卷連結',
        description: 'Email 收件人、CC 業務與範本設定；目前只做 mock-safe preview。',
        defaultVisible: false,
      },
      {
        key: 'share.qr',
        label: 'QR Code',
        description: '前端 QR 預覽與下載入口；不建立正式 QR 資產庫。',
        defaultVisible: false,
      },
      {
        key: 'share.tracking',
        label: '追蹤參數',
        description: 'UTM、hidden fields、業務代碼與活動代碼測試連結。',
        defaultVisible: false,
      },
      {
        key: 'share.contract',
        label: 'Contract Preview',
        description: '顯示分享、Email 與追蹤設定的 mock-safe payload。',
        defaultVisible: false,
      },
    ],
  },
  {
    id: 'forms',
    label: '問卷管理',
    description: '控制問卷設定、問卷管理工作區左欄頁籤與 provider-disabled preview 入口。',
    features: [
      {
        key: 'forms.analytics',
        label: '圖表統計',
        description: '逐題圖表統計與整體填答表現；預設隱藏，需由超級管理員開啟。',
        defaultVisible: false,
      },
      {
        key: 'forms.export',
        label: '明細匯出',
        description: '填寫明細表格與正式 R2 匯出入口。',
        defaultVisible: true,
      },
      {
        key: 'forms.exportLocalDownload',
        label: '瀏覽器直接下載',
        description: '明細匯出中的 CSV / JSON 瀏覽器直接下載按鈕；預設隱藏。',
        defaultVisible: false,
      },
      {
        key: 'forms.exportWorkerStream',
        label: 'Worker 串流匯出',
        description: '明細匯出中的 Worker 串流下載按鈕；預設隱藏，主要保留給除錯或管理用途。',
        defaultVisible: false,
      },
      {
        key: 'forms.exportFilters',
        label: '明細匯出欄位與篩選',
        description: '明細匯出頁面的欄位與篩選資訊區塊；預設隱藏。',
        defaultVisible: false,
      },
      {
        key: 'forms.exportStatus',
        label: '明細匯出狀態',
        description: '明細匯出頁面的 route、權限、provider 與 job 狀態區塊；預設隱藏。',
        defaultVisible: false,
      },
      {
        key: 'forms.startPage',
        label: '開始頁',
        description: '問卷設定中的開始頁編輯與「啟用開始頁」開關；關閉時問卷設定不顯示開始頁選項。',
        defaultVisible: false,
      },
      {
        key: 'forms.crossAnalysis',
        label: '交叉分析',
        description: '題目交叉分析 preview；目前維持 frontend-only mock-safe。',
        defaultVisible: false,
      },
      {
        key: 'forms.visitorActivity',
        label: '訪客紀錄',
        description: 'Session-level visitor activity preview；不啟用 analytics provider。',
        defaultVisible: false,
      },
      {
        key: 'forms.linkGenerator',
        label: '網址產生器',
        description: '批次追蹤網址與 hidden field preview；不產生正式短網址或外部連結資產。',
        defaultVisible: false,
      },
      {
        key: 'forms.completionPage',
        label: '完成頁',
        description: '問卷完成頁設定與 preview。',
        defaultVisible: true,
      },
      {
        key: 'forms.customerWorkflow',
        label: '建檔流程',
        description: 'B2B 新客建檔流程 preview；只在客戶建檔問卷中顯示。',
        defaultVisible: false,
      },
      {
        key: 'forms.customerSubmitCustomerCompareFlow',
        label: '送出問卷(比對客戶流程)',
        description:
          '保留舊版送出前比對專案客戶 / ERP 客戶的送出按鈕；預設隱藏，主要給管理員測試或回復舊流程。',
        defaultVisible: false,
      },
      {
        key: 'forms.operations',
        label: '操作設定',
        description: '封存 / 還原封存操作設定；預設不顯示，需由超級管理員開啟。',
        defaultVisible: false,
      },
      {
        key: 'forms.templates',
        label: '問卷範本',
        description: '左側問卷選單、使用範本建立問卷與編輯器存成範本功能；預設不顯示。',
        defaultVisible: false,
      },
      {
        key: 'forms.logicVariables',
        label: '變數 contract',
        description: '問卷題目邏輯編輯區中的變數 contract preview；預設不顯示。',
        defaultVisible: false,
      },
      {
        key: 'forms.logicCalculations',
        label: '計算規則',
        description: '問卷題目邏輯編輯區中的計算規則 preview；預設不顯示。',
        defaultVisible: false,
      },
      {
        key: 'forms.logicOutcomeBranches',
        label: '結果頁分流草案',
        description: '問卷題目邏輯編輯區中的結果頁分流草案 preview；預設不顯示。',
        defaultVisible: false,
      },
    ],
  },
  {
    id: 'customers',
    label: '客戶流程',
    description: '控制新客戶申請、審核中心與客戶詳情內的 workflow 級區塊。',
    features: [
      {
        key: 'customers.createApplication',
        label: '建立新客戶申請按鈕',
        description: '新客戶申請列表右上角的手動建立入口；關閉時使用者仍可檢視既有申請。',
        defaultVisible: false,
      },
      {
        key: 'customers.sourceRecords',
        label: '來源問卷記錄',
        description: '從可用問卷記錄建立新客戶申請；關閉後只保留手動建立。',
        defaultVisible: true,
      },
      {
        key: 'customers.duplicates',
        label: '重複客戶提示',
        description: '顯示 mock-safe 重複客戶偵測提示與可能重複 badge。',
        defaultVisible: true,
      },
      {
        key: 'customers.sourceFields',
        label: '來源欄位',
        description: '新客戶申請彈窗中的欄位來源對照頁籤。',
        defaultVisible: true,
      },
      {
        key: 'customers.history',
        label: '歷程記錄',
        description: '客戶詳情中的審核與歷程記錄頁籤。',
        defaultVisible: true,
      },
      {
        key: 'customers.erpFiling',
        label: 'ERP 建檔回填資訊',
        description: '客戶管理詳情中的 ERP 建檔回填欄位；不連接真實 ERP。',
        defaultVisible: true,
      },
    ],
  },
  {
    id: 'sales',
    label: '業務管理',
    description: '控制業務清單中的匯入與檔案處理入口；預設不顯示高風險匯入功能。',
    features: [
      {
        key: 'sales.importCsv',
        label: '匯入業務',
        description:
          '業務管理中的 CSV 匯入入口；預設隱藏，開啟後可由管理員手動匯入或更新業務資料。',
        defaultVisible: false,
      },
      {
        key: 'sales.fileImport',
        label: 'PDF / 圖片匯入業務資料',
        description:
          '業務管理中的 PDF / 圖片匯入入口；預設隱藏，正式 OCR / AI / R2 流程另批審核後才開啟。',
        defaultVisible: false,
      },
    ],
  },
  {
    id: 'runtime',
    label: '系統狀態',
    description: '控制 mock-safe runtime / provider 狀態提示；不在此啟用外部服務。',
    features: [
      {
        key: 'runtime.statusBadges',
        label: 'mock-safe 狀態徽章',
        description: '系統頁與設定頁的 frontend-only / mock-safe 狀態徽章。',
        defaultVisible: true,
      },
      {
        key: 'runtime.providerPlanning',
        label: 'Provider planning 狀態',
        description: '設定頁的 Email / Webhook provider planning 區塊與 not configured 狀態。',
        defaultVisible: true,
      },
      {
        key: 'runtime.productionNotice',
        label: 'Production activation 提示',
        description:
          '正式啟用前需要 RLS、Cloudflare bindings、OAuth callback 與 owner approval 的提示。',
        defaultVisible: true,
      },
    ],
  },
]

export const formManagementTabFeatureKeys: Partial<Record<FormManagementTabId, SystemFeatureKey>> =
  {
    analytics: 'forms.analytics',
    export: 'forms.export',
    crossAnalysis: 'forms.crossAnalysis',
    visitorActivity: 'forms.visitorActivity',
    linkGenerator: 'forms.linkGenerator',
    completionPage: 'forms.completionPage',
    customerWorkflow: 'forms.customerWorkflow',
    operations: 'forms.operations',
  }

export const formManagementSystemTabOrder = [
  'analytics',
  'export',
  'crossAnalysis',
  'visitorActivity',
  'linkGenerator',
  'completionPage',
  'customerWorkflow',
  'operations',
] satisfies FormManagementTabId[]

export const systemFeatureVisibilityDefaults = systemFeatureGroups.reduce((acc, group) => {
  for (const feature of group.features) {
    acc[feature.key] = feature.defaultVisible
  }
  return acc
}, {} as SystemFeatureVisibility)

export function getSystemFeatureDefinition(key: SystemFeatureKey) {
  return systemFeatureGroups
    .flatMap((group) => group.features)
    .find((feature) => feature.key === key)
}

export function getSystemFeatureGroup(groupId: SystemFeatureGroupId) {
  return systemFeatureGroups.find((group) => group.id === groupId)
}

export function getSystemFeatureGroupSummary(
  groupId: SystemFeatureGroupId,
  visibility: SystemFeatureVisibility,
): SystemFeatureGroupSummary {
  const group = getSystemFeatureGroup(groupId)
  if (!group) {
    return {
      totalCount: 0,
      visibleCount: 0,
      hiddenCount: 0,
      changedCount: 0,
    }
  }

  const visibleCount = group.features.filter((feature) => visibility[feature.key]).length
  const changedCount = group.features.filter(
    (feature) => visibility[feature.key] !== systemFeatureVisibilityDefaults[feature.key],
  ).length

  return {
    totalCount: group.features.length,
    visibleCount,
    hiddenCount: group.features.length - visibleCount,
    changedCount,
  }
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function emitSystemFeatureVisibilityChange() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(systemFeatureVisibilityEventName))
}

export function readSystemFeatureVisibility(): SystemFeatureVisibility {
  if (!canUseLocalStorage()) return { ...systemFeatureVisibilityDefaults }

  try {
    const raw = window.localStorage.getItem(systemFeatureVisibilityStorageKey)
    if (!raw) return { ...systemFeatureVisibilityDefaults }
    const parsed = JSON.parse(raw) as Partial<Record<SystemFeatureKey | string, unknown>>
    return Object.fromEntries(
      Object.entries(systemFeatureVisibilityDefaults).map(([key, defaultVisible]) => [
        key,
        typeof parsed[key] === 'boolean'
          ? parsed[key]
          : key === 'sales.fileImport' &&
              typeof parsed['runtime.salesUserFileImport'] === 'boolean'
            ? parsed['runtime.salesUserFileImport']
          : defaultVisible,
      ]),
    ) as SystemFeatureVisibility
  } catch {
    return { ...systemFeatureVisibilityDefaults }
  }
}

export function writeSystemFeatureVisibility(visibility: SystemFeatureVisibility) {
  if (!canUseLocalStorage()) return visibility
  window.localStorage.setItem(systemFeatureVisibilityStorageKey, JSON.stringify(visibility))
  emitSystemFeatureVisibilityChange()
  return visibility
}

export function setSystemFeatureVisible(key: SystemFeatureKey, visible: boolean) {
  const next = {
    ...readSystemFeatureVisibility(),
    [key]: visible,
  }
  return writeSystemFeatureVisibility(next)
}

export function resetSystemFeatureGroupVisibility(groupId: SystemFeatureGroupId) {
  const group = getSystemFeatureGroup(groupId)
  const next = { ...readSystemFeatureVisibility() }

  for (const feature of group?.features ?? []) {
    next[feature.key] = systemFeatureVisibilityDefaults[feature.key]
  }

  return writeSystemFeatureVisibility(next)
}

export function resetSystemFeatureVisibility() {
  return writeSystemFeatureVisibility({ ...systemFeatureVisibilityDefaults })
}

export function isSystemFeatureVisible(key: SystemFeatureKey) {
  return readSystemFeatureVisibility()[key]
}

export function subscribeSystemFeatureVisibility(listener: () => void) {
  if (typeof window === 'undefined') return () => {}

  const handleStorage = (event: StorageEvent) => {
    if (event.key === systemFeatureVisibilityStorageKey) listener()
  }

  window.addEventListener(systemFeatureVisibilityEventName, listener)
  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener(systemFeatureVisibilityEventName, listener)
    window.removeEventListener('storage', handleStorage)
  }
}

export function useSystemFeatureVisibility() {
  const [visibility, setVisibility] = useState<SystemFeatureVisibility>(() =>
    readSystemFeatureVisibility(),
  )

  useEffect(() => {
    return subscribeSystemFeatureVisibility(() => {
      setVisibility(readSystemFeatureVisibility())
    })
  }, [])

  return visibility
}

export function resetSystemFeatureVisibilityForTests() {
  if (canUseLocalStorage()) {
    window.localStorage.removeItem(systemFeatureVisibilityStorageKey)
  }
  emitSystemFeatureVisibilityChange()
}
